import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  Connection,
  useReactFlow,
} from "@xyflow/react";
import { useSelector } from "react-redux";
import type { MouseEvent as ReactMouseEvent } from "react";
import {
  selectSelectedElement,
  selectSelectedElementType,
} from "../../app/slices/TrainingSlice";
import { ISequence } from "../../models/interactions/Sequence";
import { SequenceCommentZone } from "../../models/interactions/Sequence";
import { SequenceTextbox } from "../../models/interactions/Sequence";
import { SequenceNodeData } from "./SequenceNode";
import { InteractionDefintion } from "../interaction_picker/InteractionDefinitions";
import { useTrainingLocalization } from "../trainingLocalization/TrainingLocalizationHooks";
import {
  applySingleConnectionRules,
  buildNodeTypeByIdMap,
} from "./SequenceCanvasConnectionRules";
import {
  createTemporaryConnectionEdge,
  createTemporaryTargetNode,
  TEMP_TARGET_NODE_ID,
} from "./SequenceCanvasTemporaryElements";
import {
  buildEdgesForAddedNode,
  createSequenceNode,
} from "./SequenceCanvasNodeCreation";
import { isTemporaryEdge } from "./SequenceCanvasSplice";
import { useSequenceCanvasAltSplice } from "./useSequenceCanvasAltSplice";
import { useSequenceCanvasConnectionLifecycle } from "./useSequenceCanvasConnectionLifecycle";
import { useUpdateActiveSequence } from "./useUpdateActiveSequence.hook";
import {
  getVisibleSequenceEdges,
  getVisibleSequenceNodes,
  isProtectedTakeSequenceTrigger,
  isSequenceRootNode,
} from "./takeSequenceEditor";
import type { SequenceCanvasSelection } from "./SequenceCanvasSelection.hook";

export function useSequenceCanvas() {
  const interaction = useSelector(selectSelectedElement) as ISequence;
  const selectedElementType = useSelector(selectSelectedElementType);
  const { screenToFlowPosition } = useReactFlow();
  const { updateSequence } = useUpdateActiveSequence();

  // Initialize with empty arrays first
  const [nodes, setNodes] = useState<Node<SequenceNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [commentZones, setCommentZones] = useState<SequenceCommentZone[]>([]);
  const [textboxes, setTextboxes] = useState<SequenceTextbox[]>([]);
  const nodesRef = useRef<Node<SequenceNodeData>[]>([]);
  const edgesRef = useRef<Edge[]>([]);
  const commentZonesRef = useRef<SequenceCommentZone[]>([]);
  const textboxesRef = useRef<SequenceTextbox[]>([]);

  const { GetArrayOfKeys } = useTrainingLocalization();
  const nodeTypeById = useMemo(
    () => buildNodeTypeByIdMap(interaction.nodes),
    [interaction.nodes],
  );
  const {
    menu,
    pendingConnectionSource,
    temporaryEdgeTarget,
    temporaryEdgeIdRef,
    edgeToSplit,
    clearPendingConnection,
    onPaneContextMenu,
    onCloseMenu,
    onConnectStart,
    onConnectEnd,
    onEdgeClick,
  } = useSequenceCanvasConnectionLifecycle({
    edges,
    screenToFlowPosition,
  });

  const {
    activeAltDragRef,
    syncDraggedNodeFromNodes,
    pruneEdgeGeometry,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onEdgePathGeometryChange,
    onEdgePathGeometryRemove,
  } = useSequenceCanvasAltSplice({
    interaction,
    updateSequence,
    nodesRef,
    edgesRef,
    setNodes,
    setEdges,
  });

  // Sync nodes and edges from Redux with mutable copies
  // Filter out temporary edges and nodes when syncing from Redux
  useEffect(() => {
    if (interaction.nodes) {
      // Filter out temporary nodes (they should only exist in local state)
      const realNodes = interaction.nodes.filter(
        (node) => node.id !== TEMP_TARGET_NODE_ID,
      );
      setNodes(
        getVisibleSequenceNodes(realNodes, selectedElementType).map((node) => ({
          ...node,
          position: { ...node.position },
          data: { ...node.data, parentSequenceId: interaction.id },
        })),
      );
    }
    if (interaction.edges) {
      // Filter out temporary edges (they should only exist in local state)
      const realEdges = interaction.edges.filter(
        (edge) => !edge.id?.startsWith("temp-connection-"),
      );
      setEdges(
        getVisibleSequenceEdges(
          interaction.nodes ?? [],
          realEdges,
          selectedElementType,
        ).map((edge) => ({ ...edge })),
      );
    }
    setCommentZones(
      (interaction.commentZones ?? []).map((zone) => ({
        ...zone,
        position: { ...zone.position },
        size: { ...zone.size },
      })),
    );
    setTextboxes(
      (interaction.textboxes ?? []).map((textbox) => ({
        ...textbox,
        position: { ...textbox.position },
        size: { ...textbox.size },
      })),
    );
  }, [interaction, selectedElementType]); // Only re-sync when interaction changes

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  useEffect(() => {
    commentZonesRef.current = commentZones;
  }, [commentZones]);

  useEffect(() => {
    textboxesRef.current = textboxes;
  }, [textboxes]);

  useEffect(() => {
    pruneEdgeGeometry(edges);
  }, [edges, pruneEdgeGeometry]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // when we dragEnd, we want to update the slice. this automatically triggers the sync of the local state
      // if we drag, we want to update the local state immediately and skip the slice update
      // check if drag ended
      const isDragEnd = changes.some(
        (change) => change.type === "position" && change.dragging === false,
      );
      const isNodeRemoval = changes.some((change) => change.type === "remove");
      const hasActiveAltDrag =
        activeAltDragRef.current !== null &&
        activeAltDragRef.current.phase !== "normal";

      const protectedNodeIds = new Set(
        nodesRef.current
          .filter((node) =>
            isProtectedTakeSequenceTrigger(node, selectedElementType),
          )
          .map((node) => node.id),
      );
      const filteredChanges = changes.filter((change) => {
        if (change.type !== "remove" || !("id" in change)) {
          return true;
        }
        return !protectedNodeIds.has(change.id);
      });

      // Persist drag end and node removal changes to Redux.
      if ((isDragEnd && !hasActiveAltDrag) || isNodeRemoval) {
        const sliceChanges = applyNodeChanges(
          filteredChanges,
          nodesRef.current,
        ) as Node<SequenceNodeData>[];
        const remainingNodeIds = new Set(sliceChanges.map((node) => node.id));
        const updatedEdges = (interaction.edges ?? []).filter(
          (edge) =>
            remainingNodeIds.has(edge.source) &&
            remainingNodeIds.has(edge.target),
        );
        updateSequence({
          ...interaction,
          nodes: sliceChanges,
          edges: updatedEdges,
        } as ISequence);
        setEdges(updatedEdges);
        return;
      }

      setNodes((nds) => {
        // Use structuredClone to create a completely independent copy
        const mutableNodes = structuredClone(nds);

        const appliedChanges = applyNodeChanges(
          filteredChanges,
          mutableNodes,
        ) as Node<SequenceNodeData>[];

        syncDraggedNodeFromNodes(appliedChanges);

        return appliedChanges;
      });
    },
    [
      interaction,
      selectedElementType,
      syncDraggedNodeFromNodes,
      updateSequence,
    ],
  );

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    // dispatch goes here if needed
  }, []);

  const onConnect = useCallback(
    (connection: Connection) => {
      // Only process if connection has both source and target
      if (!connection.source || !connection.target) {
        return;
      }

      clearPendingConnection();

      const currentEdges = interaction.edges || [];
      const filteredEdges = applySingleConnectionRules(
        currentEdges,
        connection,
        nodeTypeById,
      );
      const newEdges = addEdge(
        { ...connection, type: "custom" },
        filteredEdges,
      );
      updateSequence({
        ...interaction,
        edges: newEdges,
      } as ISequence);
    },
    [clearPendingConnection, interaction, nodeTypeById, updateSequence],
  );

  const onAddNode = useCallback(
    (definition: InteractionDefintion) => {
      if (!menu) return;

      const position = screenToFlowPosition({
        x: menu.screenX,
        y: menu.screenY,
      });

      const newNode = createSequenceNode(definition, position, GetArrayOfKeys);
      const newEdges = buildEdgesForAddedNode({
        existingEdges: interaction.edges,
        newNodeId: newNode.id,
        edgeToSplit,
        pendingConnectionSource,
        nodeTypeById,
      });

      // Dispatch both node and edges updates (interaction here is the ISequence from closure)
      updateSequence({
        ...interaction,
        nodes: [...(interaction.nodes ?? []), newNode],
        edges: newEdges,
      } as ISequence);

      onCloseMenu();
    },
    [
      menu,
      screenToFlowPosition,
      GetArrayOfKeys,
      interaction,
      onCloseMenu,
      pendingConnectionSource,
      edgeToSplit,
      nodeTypeById,
      updateSequence,
    ],
  );

  const onDeleteEdges = useCallback(
    (edgesToDelete: Edge[]) => {
      if (edgesToDelete.length === 0) return;

      const newEdges = edges.filter((edge) => !edgesToDelete.includes(edge));
      updateSequence({
        ...interaction,
        edges: newEdges,
      } as ISequence);
    },
    [edges, interaction, updateSequence],
  );

  const onSetNodeColor = useCallback(
    (nodeIds: string[], color: string | null) => {
      if (!interaction.nodes || nodeIds.length === 0) return;
      const idSet = new Set(nodeIds);
      const newNodes = interaction.nodes.map((node) => {
        if (!idSet.has(node.id)) return node;
        const { color: _removed, ...restData } = node.data;
        const data = color !== null ? { ...restData, color } : restData;
        return { ...node, data };
      });
      updateSequence({
        ...interaction,
        nodes: newNodes,
      } as ISequence);
    },
    [interaction, updateSequence],
  );

  const setLocalNodes = useCallback(
    (
      updater:
        | Node<SequenceNodeData>[]
        | ((current: Node<SequenceNodeData>[]) => Node<SequenceNodeData>[]),
    ) => {
      setNodes((current) => {
        const nextNodes =
          typeof updater === "function" ? updater(current) : updater;
        nodesRef.current = nextNodes;
        return nextNodes;
      });
    },
    [],
  );

  const setLocalCommentZones = useCallback(
    (
      updater:
        | SequenceCommentZone[]
        | ((current: SequenceCommentZone[]) => SequenceCommentZone[]),
    ) => {
      setCommentZones((current) => {
        const nextCommentZones =
          typeof updater === "function" ? updater(current) : updater;
        commentZonesRef.current = nextCommentZones;
        return nextCommentZones;
      });
    },
    [],
  );

  const setLocalTextboxes = useCallback(
    (
      updater:
        SequenceTextbox[] | ((current: SequenceTextbox[]) => SequenceTextbox[]),
    ) => {
      setTextboxes((current) => {
        const nextTextboxes =
          typeof updater === "function" ? updater(current) : updater;
        textboxesRef.current = nextTextboxes;
        return nextTextboxes;
      });
    },
    [],
  );

  const persistCanvasState = useCallback(
    ({
      nextNodes = nodesRef.current,
      nextEdges = edgesRef.current,
      nextCommentZones = commentZonesRef.current,
      nextTextboxes = textboxesRef.current,
    }: {
      nextNodes?: Node<SequenceNodeData>[];
      nextEdges?: Edge[];
      nextCommentZones?: SequenceCommentZone[];
      nextTextboxes?: SequenceTextbox[];
    }) => {
      updateSequence({
        ...interaction,
        nodes: nextNodes,
        edges: nextEdges,
        commentZones: nextCommentZones,
        textboxes: nextTextboxes,
      } as ISequence);
    },
    [interaction, updateSequence],
  );

  const onCreateCommentZone = useCallback(
    (zone: SequenceCommentZone) => {
      const nextCommentZones = [...commentZonesRef.current, zone];
      commentZonesRef.current = nextCommentZones;
      setCommentZones(nextCommentZones);
      persistCanvasState({ nextCommentZones });
    },
    [persistCanvasState],
  );

  const onSetCommentZoneColor = useCallback(
    (zoneIds: string[], color: string | null) => {
      if (zoneIds.length === 0) return;
      const zoneIdSet = new Set(zoneIds);
      const nextCommentZones = commentZonesRef.current.map((zone) => {
        if (!zoneIdSet.has(zone.id)) return zone;
        if (color === null) {
          const { color: _removed, ...rest } = zone;
          return rest;
        }
        return { ...zone, color };
      });
      commentZonesRef.current = nextCommentZones;
      setCommentZones(nextCommentZones);
      persistCanvasState({ nextCommentZones });
    },
    [persistCanvasState],
  );

  const onSetTextboxColor = useCallback(
    (textboxIds: string[], color: string | null) => {
      if (textboxIds.length === 0) return;
      const textboxIdSet = new Set(textboxIds);
      const nextTextboxes = textboxesRef.current.map((textbox) => {
        if (!textboxIdSet.has(textbox.id)) return textbox;
        if (color === null) {
          const { color: _removed, ...rest } = textbox;
          return rest;
        }
        return { ...textbox, color };
      });
      textboxesRef.current = nextTextboxes;
      setTextboxes(nextTextboxes);
      persistCanvasState({ nextTextboxes });
    },
    [persistCanvasState],
  );

  const onDeleteCommentZones = useCallback(
    (zoneIds: string[]) => {
      if (zoneIds.length === 0) return;
      const zoneIdSet = new Set(zoneIds);
      const nextCommentZones = commentZonesRef.current.filter(
        (zone) => !zoneIdSet.has(zone.id),
      );
      commentZonesRef.current = nextCommentZones;
      setCommentZones(nextCommentZones);
      persistCanvasState({ nextCommentZones });
    },
    [persistCanvasState],
  );

  const onUpdateCommentZone = useCallback(
    (zoneId: string, updates: Partial<SequenceCommentZone>, persist = true) => {
      const nextCommentZones = commentZonesRef.current.map((zone) =>
        zone.id === zoneId
          ? {
              ...zone,
              ...updates,
              position: updates.position
                ? { ...updates.position }
                : { ...zone.position },
              size: updates.size ? { ...updates.size } : { ...zone.size },
            }
          : zone,
      );
      commentZonesRef.current = nextCommentZones;
      setCommentZones(nextCommentZones);
      if (persist) {
        persistCanvasState({ nextCommentZones });
      }
    },
    [persistCanvasState],
  );

  const onCreateTextbox = useCallback(
    (textbox: SequenceTextbox) => {
      const nextTextboxes = [...textboxesRef.current, textbox];
      textboxesRef.current = nextTextboxes;
      setTextboxes(nextTextboxes);
      persistCanvasState({ nextTextboxes });
    },
    [persistCanvasState],
  );

  const onDeleteTextboxes = useCallback(
    (textboxIds: string[]) => {
      if (textboxIds.length === 0) return;
      const textboxIdSet = new Set(textboxIds);
      const nextTextboxes = textboxesRef.current.filter(
        (textbox) => !textboxIdSet.has(textbox.id),
      );
      textboxesRef.current = nextTextboxes;
      setTextboxes(nextTextboxes);
      persistCanvasState({ nextTextboxes });
    },
    [persistCanvasState],
  );

  const onDeleteSelection = useCallback(
    (selection: SequenceCanvasSelection, selectedEdgeIds: string[]) => {
      const selectedNodeIds = new Set(selection.nodeIds);
      const deletableNodeIds = new Set(
        nodesRef.current
          .filter(
            (node) =>
              selectedNodeIds.has(node.id) &&
              node.id !== TEMP_TARGET_NODE_ID &&
              !isSequenceRootNode(node) &&
              !isProtectedTakeSequenceTrigger(node, selectedElementType),
          )
          .map((node) => node.id),
      );
      const commentZoneIds = new Set(selection.commentZoneIds);
      const textboxIds = new Set(selection.textboxIds);
      const edgeIds = new Set(selectedEdgeIds);

      const removedCommentZoneIds = commentZonesRef.current
        .filter((zone) => commentZoneIds.has(zone.id))
        .map((zone) => zone.id);
      const removedTextboxIds = textboxesRef.current
        .filter((textbox) => textboxIds.has(textbox.id))
        .map((textbox) => textbox.id);
      const hasSelectedEdge = edgesRef.current.some((edge) =>
        edgeIds.has(edge.id),
      );

      if (
        deletableNodeIds.size === 0 &&
        removedCommentZoneIds.length === 0 &&
        removedTextboxIds.length === 0 &&
        !hasSelectedEdge
      ) {
        return {
          didPersist: false,
          removedSelection: {
            nodeIds: [],
            commentZoneIds: [],
            textboxIds: [],
          } satisfies SequenceCanvasSelection,
        };
      }

      const nextNodes = nodesRef.current.filter(
        (node) => !deletableNodeIds.has(node.id),
      );
      const nextEdges = edgesRef.current.filter(
        (edge) =>
          !edgeIds.has(edge.id) &&
          !deletableNodeIds.has(edge.source) &&
          !deletableNodeIds.has(edge.target),
      );
      const nextCommentZones = commentZonesRef.current.filter(
        (zone) => !commentZoneIds.has(zone.id),
      );
      const nextTextboxes = textboxesRef.current.filter(
        (textbox) => !textboxIds.has(textbox.id),
      );

      nodesRef.current = nextNodes;
      edgesRef.current = nextEdges;
      commentZonesRef.current = nextCommentZones;
      textboxesRef.current = nextTextboxes;
      setNodes(nextNodes);
      setEdges(nextEdges);
      setCommentZones(nextCommentZones);
      setTextboxes(nextTextboxes);
      persistCanvasState({
        nextNodes,
        nextEdges,
        nextCommentZones,
        nextTextboxes,
      });

      return {
        didPersist: true,
        removedSelection: {
          nodeIds: [...deletableNodeIds],
          commentZoneIds: removedCommentZoneIds,
          textboxIds: removedTextboxIds,
        } satisfies SequenceCanvasSelection,
      };
    },
    [persistCanvasState, selectedElementType],
  );

  const onSetSelectionColor = useCallback(
    (selection: SequenceCanvasSelection, color: string | null) => {
      if (
        selection.nodeIds.length === 0 &&
        selection.commentZoneIds.length === 0 &&
        selection.textboxIds.length === 0
      ) {
        return;
      }

      const nodeIds = new Set(selection.nodeIds);
      const commentZoneIds = new Set(selection.commentZoneIds);
      const textboxIds = new Set(selection.textboxIds);
      const nextNodes = nodesRef.current.map((node) => {
        if (!nodeIds.has(node.id)) return node;
        const { color: _removed, ...restData } = node.data;
        return {
          ...node,
          data: color === null ? restData : { ...restData, color },
        };
      });
      const nextCommentZones = commentZonesRef.current.map((zone) => {
        if (!commentZoneIds.has(zone.id)) return zone;
        if (color === null) {
          const { color: _removed, ...rest } = zone;
          return rest;
        }
        return { ...zone, color };
      });
      const nextTextboxes = textboxesRef.current.map((textbox) => {
        if (!textboxIds.has(textbox.id)) return textbox;
        if (color === null) {
          const { color: _removed, ...rest } = textbox;
          return rest;
        }
        return { ...textbox, color };
      });

      nodesRef.current = nextNodes;
      commentZonesRef.current = nextCommentZones;
      textboxesRef.current = nextTextboxes;
      setNodes(nextNodes);
      setCommentZones(nextCommentZones);
      setTextboxes(nextTextboxes);
      persistCanvasState({ nextNodes, nextCommentZones, nextTextboxes });
    },
    [persistCanvasState],
  );

  const onUpdateTextbox = useCallback(
    (textboxId: string, updates: Partial<SequenceTextbox>, persist = true) => {
      const nextTextboxes = textboxesRef.current.map((textbox) =>
        textbox.id === textboxId
          ? {
              ...textbox,
              ...updates,
              position: updates.position
                ? { ...updates.position }
                : { ...textbox.position },
              size: updates.size ? { ...updates.size } : { ...textbox.size },
            }
          : textbox,
      );
      textboxesRef.current = nextTextboxes;
      setTextboxes(nextTextboxes);
      if (persist) {
        persistCanvasState({ nextTextboxes });
      }
    },
    [persistCanvasState],
  );

  // Combine real nodes/edges with temporary node and edge if menu is open
  const displayNodes = useMemo(() => {
    if (!temporaryEdgeTarget || !pendingConnectionSource) {
      return nodes;
    }

    const tempNode = createTemporaryTargetNode(temporaryEdgeTarget);

    return [...nodes, tempNode];
  }, [nodes, temporaryEdgeTarget, pendingConnectionSource]);

  const displayEdges = useMemo(() => {
    // Show temporary edge while menu is open (when temporaryEdgeTarget exists)
    if (
      !temporaryEdgeTarget ||
      !pendingConnectionSource ||
      !temporaryEdgeIdRef.current
    ) {
      return edges;
    }

    const tempEdge = createTemporaryConnectionEdge(
      pendingConnectionSource,
      temporaryEdgeIdRef.current,
    );
    if (!tempEdge) {
      return edges;
    }
    return [...edges, tempEdge];
  }, [edges, temporaryEdgeTarget, pendingConnectionSource]);

  return {
    sequenceId: interaction.id,
    nodes: displayNodes,
    edges: displayEdges,
    commentZones,
    textboxes,
    menu,
    pendingConnection: pendingConnectionSource,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onPaneContextMenu,
    onCloseMenu,
    onAddNode,
    onDeleteEdges,
    onSetNodeColor,
    onCreateCommentZone,
    onSetCommentZoneColor,
    onSetTextboxColor,
    onDeleteCommentZones,
    onUpdateCommentZone,
    onCreateTextbox,
    onDeleteTextboxes,
    onDeleteSelection,
    onSetSelectionColor,
    onUpdateTextbox,
    setLocalNodes,
    setLocalCommentZones,
    setLocalTextboxes,
    persistCanvasState,
    onConnectStart,
    onConnectEnd,
    onEdgeClick,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onEdgePathGeometryChange,
    onEdgePathGeometryRemove,
  };
}
